// ==========================================================================
// Brand Store - Whitelabeling state management
// ==========================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BrandConfig } from '@/types/brand';
import { fetchBrandConfig } from '@/services/brand.service';

// Default brand configuration
const defaultBrand: BrandConfig = {
  id: 'fortune-credit',
  name: 'fortune-credit',
  displayName: 'Fortune Credit',
  colors: {
    primary: '#ED974F',
    primaryHover: '#d9863f',
    secondary: '#c47535',
    secondaryHover: '#b0682e',
  },
  assets: {
    logo: '/assets/logo.svg',
    favicon: '/favicon.png',
    appIcon192: '/pwa-192x192.png',
    appIcon512: '/pwa-512x512.png',
  },
  pwa: {
    name: 'Fortune Credit',
    shortName: 'Fortune',
    themeColor: '#ED974F',
    backgroundColor: '#ffffff',
  },
  features: {
    notifications: true,
    geolocation: true,
  },
};

interface BrandState {
  brandId: string | null;
  config: BrandConfig;
  isLoading: boolean;
  error: string | null;
  initializeBrand: () => Promise<void>;
  setBrand: (config: BrandConfig) => void;
  resetToDefault: () => void;
}

function applyBrandColors(colors: BrandConfig['colors']) {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary', colors.primary);
  root.style.setProperty('--brand-primary-hover', colors.primaryHover);
  root.style.setProperty('--brand-secondary', colors.secondary);
  root.style.setProperty('--brand-secondary-hover', colors.secondaryHover);
  // Focus ring uses brand primary color
  root.style.setProperty('--color-ring', colors.primary);
}

function updateFavicon(faviconUrl: string) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (link) link.href = faviconUrl;
}

function updateThemeColor(color: string) {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = color;
}

export const useBrandStore = create<BrandState>()(
  persist(
    (set, get) => ({
      brandId: null,
      config: defaultBrand,
      isLoading: false,
      error: null,

      initializeBrand: async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const fspId = urlParams.get('fsp') || urlParams.get('brand');

        if (fspId && fspId !== get().brandId) {
          set({ isLoading: true, error: null });

          try {
            const brandConfig = await fetchBrandConfig(fspId);
            
            applyBrandColors(brandConfig.colors);
            updateFavicon(brandConfig.assets.favicon);
            if (brandConfig.pwa?.themeColor) {
              updateThemeColor(brandConfig.pwa.themeColor);
            }

            set({ brandId: fspId, config: brandConfig, isLoading: false });
          } catch {
            // Toast shown by axios interceptor
            // Fall back to default
            const currentConfig = get().config;
            applyBrandColors(currentConfig.colors);
            updateFavicon(currentConfig.assets.favicon);
            set({ error: 'Failed to load brand configuration', isLoading: false });
          }
        } else {
          // Apply current/default config
          const currentConfig = get().config;
          applyBrandColors(currentConfig.colors);
          updateFavicon(currentConfig.assets.favicon);
        }
      },

      setBrand: (config: BrandConfig) => {
        applyBrandColors(config.colors);
        updateFavicon(config.assets.favicon);
        if (config.pwa?.themeColor) {
          updateThemeColor(config.pwa.themeColor);
        }
        set({ brandId: config.id, config, error: null });
      },

      resetToDefault: () => {
        applyBrandColors(defaultBrand.colors);
        updateFavicon(defaultBrand.assets.favicon);
        set({ brandId: null, config: defaultBrand, error: null });
      },
    }),
    {
      name: 'yaya-brand',
      version: 2,
      partialize: (state) => ({
        brandId: state.brandId,
        config: state.config,
      }),
    }
  )
);
